import { useEffect, useState } from 'react';

/**
 * The forty seconds a build takes.
 *
 * Previously this was a disabled button reading "Building…" and nothing else,
 * which for a nine-year-old is indistinguishable from broken. Three things fix
 * that: something moving, a sense of progress, and — the useful bit — a running
 * commentary of what is actually happening, so the wait teaches rather than
 * merely passes.
 *
 * The steps are timed rather than reported by the server. They are honest about
 * the order things happen in, and the last one waits rather than claiming to
 * have finished, so a slow build never shows a lie.
 */
const STEPS = [
  { at: 0,   label: 'Reading your idea' },
  { at: 4,   label: 'Checking it is a good one' },
  { at: 9,   label: 'Choosing how it should look' },
  { at: 16,  label: 'Writing the code' },
  { at: 30,  label: 'Making sure it is safe to play' },
  { at: 38,  label: 'Almost there' },
];

const FACTS = [
  'Your game is being written in a language called JavaScript.',
  'Every game you make is a real web page — you could open it on any computer.',
  'The clearer your idea, the closer the game will be to what you pictured.',
  'You can change any number in your game afterwards, for free.',
  'Nobody has ever made exactly this game before. It is yours.',
  'A computer reads your whole game about sixty times every second.',
];

export function BuildingScreen({ what }: { what: string }) {
  const [seconds, setSeconds] = useState(0);
  const [fact, setFact] = useState(() => Math.floor(Math.random() * FACTS.length));

  // Ticks five times a second against a real start time, rather than counting
  // intervals. Two reasons: it cannot drift if the tab is throttled, and the
  // bar moves smoothly without a CSS transition. A one-second transition
  // replaced every one second is a race that loses — it was leaving the bar
  // pinned at zero for the whole build.
  useEffect(() => {
    const startedAt = Date.now();
    const tick = setInterval(() => setSeconds((Date.now() - startedAt) / 1000), 200);
    const rotate = setInterval(() => setFact((f) => (f + 1) % FACTS.length), 9000);
    return () => {
      clearInterval(tick);
      clearInterval(rotate);
    };
  }, []);

  const done = STEPS.filter((s) => seconds >= s.at);
  const current = done[done.length - 1] ?? STEPS[0]!;
  // Eases towards full but never reaches it, so a slow build is never a lie.
  const percent = Math.min(96, 100 * (1 - Math.exp(-seconds / 17)));

  return (
    <section className="building">
      <div className="building__stage">
        <div className="building__orb" />
        {[0, 1, 2, 3, 4, 5].map((i) => (
          <span key={i} className="building__spark" style={{ ['--i' as string]: i }} />
        ))}
      </div>

      <h2>Building “{what}”</h2>

      <div className="building__bar">
        {/* Scaled rather than resized: animating width from 0% on a freshly
            inserted element leaves it latched at zero, and transform is the
            cheaper property to animate besides. */}
        <div className="building__fill" style={{ transform: `scaleX(${percent / 100})` }} />
      </div>

      <ol className="building__steps">
        {STEPS.map((step) => {
          const state = seconds >= step.at ? (step === current ? 'now' : 'done') : 'todo';
          return (
            <li key={step.label} className={`building__step building__step--${state}`}>
              <span className="building__tick">{state === 'done' ? '✓' : state === 'now' ? '›' : ''}</span>
              {step.label}
            </li>
          );
        })}
      </ol>

      <p className="building__fact">{FACTS[fact]}</p>
    </section>
  );
}
